import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { range } from 'lodash';

const SuccessionContinuingPayments = ({ query, updateCfaQuery, intl }) => {
  // Local state for percentage inputs to avoid reformatting during typing
  const [percentageInputs, setPercentageInputs] = useState({});
  
  // Initialize percentage inputs from query
  useEffect(() => {
    if (query.buyerContinuingPayments && query.buyerContinuingPayments.length > 0) {
      const newPercentageInputs = {};
      query.buyerContinuingPayments.forEach((payment, index) => {
        if (payment !== undefined) {
          newPercentageInputs[`payment_${index}`] = (payment * 100).toFixed(1);
        }
      });
      setPercentageInputs(newPercentageInputs);
    }
  }, [query.buyerContinuingPaymentYears]);

  const validate = () => {
    let valid = true;

    if (query.buyerHasContinuingPayments == undefined) {
      valid = false;
    } else if (query.buyerHasContinuingPayments) {
      if (!query.buyerContinuingPaymentYears || parseFloat(query.buyerContinuingPaymentYears) < 1) {
        valid = false;
      }

      if ((query.buyerContinuingPayments || []).length < 1) {
        valid = false;
      }
      
      (query.buyerContinuingPayments || []).forEach((payment) => {
        if (parseFloat(payment) <= 0) {
          valid = false;
        }
      });
    }

    if (query.valid != valid) {
      updateCfaQuery({ ...query, valid });
    }
  };

  // Call validate on every render (matching original behavior)
  validate();

  const handlePercentageChange = (index, displayValue) => {
    // Update local state for display
    setPercentageInputs(prev => ({
      ...prev,
      [`payment_${index}`]: displayValue
    }));
    
    // Update query with decimal value if it's a valid number
    const numValue = parseFloat(displayValue);
    if (!isNaN(numValue)) {
      // Get the maximum allowed value (previous payment if exists, otherwise 100%)
      const maxValue = index > 0 ? (query.buyerContinuingPayments[index - 1] || 0) * 100 : 100;
      const clampedValue = Math.max(0, Math.min(maxValue, numValue));
      const decimalValue = clampedValue / 100;
      
      let payments = [...(query.buyerContinuingPayments || [])];
      payments[index] = decimalValue;
      updateCfaQuery({ ...query, buyerContinuingPayments: payments });
      
      // Update the display value to the clamped value if it was changed
      if (clampedValue !== numValue) {
        setPercentageInputs(prev => ({
          ...prev,
          [`payment_${index}`]: clampedValue.toString()
        }));
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        <FormattedMessage id="calculators.cfa.what_are_term_of_deal" />
      </h2>
      <p className="text-gray-600 mb-8">
        <FormattedMessage id="calculators.cfa.cont_payment_copy" />
      </p>
      
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          <FormattedMessage id="calculators.cfa.cont_payments" />
        </h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <FormattedMessage id="calculators.cfa.will_cont_payments" />
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buyerHasContinuingPayments"
                  checked={query.buyerHasContinuingPayments === true}
                  onChange={() => {
                    updateCfaQuery({ ...query, buyerHasContinuingPayments: true });
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">
                  <FormattedMessage id="calculators.cfa.yes_label" />
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="buyerHasContinuingPayments"
                  checked={query.buyerHasContinuingPayments === false}
                  onChange={() => {
                    updateCfaQuery({ 
                      ...query, 
                      buyerHasContinuingPayments: false,
                      buyerContinuingPaymentAmount: 0,
                      buyerContinuingPaymentYears: 0,
                      buyerContinuingPaymentStartYear: 0,
                      buyerContinuingPayments: []
                    });
                    setPercentageInputs({});
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">
                  <FormattedMessage id="calculators.cfa.no_label" />
                </span>
              </label>
            </div>
          </div>

          {query.buyerHasContinuingPayments && (
            <div className="space-y-6 ml-6 border-l-2 border-gray-200 pl-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FormattedMessage id="calculators.cfa.how_many_years_cont" />
                </label>
                <select
                  value={query.buyerContinuingPaymentYears || ''}
                  onChange={(e) => {
                    const newYears = parseInt(e.target.value);
                    let buyerContinuingPayments = query.buyerContinuingPayments || [];
                    
                    if (query.buyerContinuingPaymentYears > newYears) {
                      // Reduce array size
                      buyerContinuingPayments = buyerContinuingPayments.slice(0, newYears);
                    } else if (query.buyerContinuingPaymentYears < newYears) {
                      // Increase array size
                      buyerContinuingPayments = [...buyerContinuingPayments, ...Array(newYears - query.buyerContinuingPaymentYears).fill(0)];
                    }
                    
                    updateCfaQuery({ 
                      ...query, 
                      buyerContinuingPaymentYears: newYears,
                      buyerContinuingPayments
                    });
                  }}
                  className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">
                    {intl.formatMessage({ id: "calculators.cfa.please_select_a_year" })}
                  </option>
                  {range(1, 6).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FormattedMessage id="calculators.cfa.cont_start_year" />
                </label>
                <select
                  value={query.buyerContinuingPaymentStartYear || ''}
                  onChange={(e) => {
                    updateCfaQuery({ ...query, buyerContinuingPaymentStartYear: parseInt(e.target.value) });
                  }}
                  className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">
                    {intl.formatMessage({ id: "calculators.cfa.please_select_a_year" })}
                  </option>
                  {range(1, 6).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {/* Dynamic Payment Inputs */}
              {(query.buyerContinuingPaymentStartYear && query.buyerContinuingPaymentYears) && (
                <div className="space-y-4">
                  <div className="text-sm font-medium text-gray-700 mb-3">
                    <FormattedMessage id="calculators.cfa.cont_payment_per_year" />
                  </div>
                  
                  {Array(query.buyerContinuingPaymentYears).fill(0).map((_, i) => {
                    const year = query.buyerContinuingPaymentStartYear + i;
                    const maxValue = i > 0 ? (query.buyerContinuingPayments[i - 1] || 0) * 100 : 100;
                    
                    return (
                      <div key={`cpYear${i}`} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {intl.formatMessage({ id: 'succession.cash_flow_analysis.continuing_payments.year_amount_label' }, { year })} <FormattedMessage id="calculators.cfa.amount" />
                        </label>
                        <div className="relative max-w-32">
                          <input
                            type="number"
                            min="0"
                            max={maxValue}
                            step="0.1"
                            value={percentageInputs[`payment_${i}`] || ''}
                            onChange={(e) => {
                              handlePercentageChange(i, e.target.value);
                            }}
                            onFocus={(e) => e.target.select()}
                            className="block w-full pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="0.0"
                          />
                          <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500">%</span>
                        </div>
                        {i > 0 && (
                          <div className="mt-1 text-xs text-gray-500">
                            {intl.formatMessage({ id: 'succession.cash_flow_analysis.continuing_payments.maximum_note' }, { max: maxValue.toFixed(1) })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default injectIntl(SuccessionContinuingPayments); 